<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import SidebarDropdown from '@/components/EventSearch/SidebarDropdown.vue'
import SaveSearchDropdown from '@/components/EventSearch/SaveSearchDropdown.vue'
import ZeekFieldGuide from '@/components/Query/ZeekFieldGuide.vue'
import { useMainStore } from '@/stores/main'

const mainStore = useMainStore()
const route = useRoute()

const tableLink = computed(() => ({ path: '/search/event/table', query: route.query }))
const aggregationsLink = computed(() => ({ path: '/search/event/aggregations', query: route.query }))
const historyLink = computed(() => ({ path: '/search/event/history', query: route.query }))

const props = defineProps({
  hasExecutedQuery: {
    type: Boolean,
    default: false
  },
  canExport: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  exportLoading: {
    type: Boolean,
    default: false
  },
  logTypeDistribution: {
    type: Array,
    default: () => []
  },
  // Saved search props
  savedSearchId: {
    type: String,
    default: null
  },
  savedSearchName: {
    type: String,
    default: ''
  },
  savedSearchDescription: {
    type: String,
    default: ''
  },
  savedSearchQuery: {
    type: String,
    default: ''
  },
  currentQuery: {
    type: String,
    default: ''
  },
  saveLoading: {
    type: Boolean,
    default: false
  },
  deleteLoading: {
    type: Boolean,
    default: false
  },
  // Subscription props
  isSubscribed: {
    type: Boolean,
    default: false
  },
  subscriptionLoading: {
    type: Boolean,
    default: false
  }
})

// Sidebar expansion state
const isExpanded = ref(false)
const selectedFormat = ref('jsonl')
const emit = defineEmits(['update:expanded', 'export', 'export-log-type', 'save-search', 'overwrite-search', 'delete-search', 'subscribe', 'unsubscribe'])

// Log type colors (matches LogTypesSidebar and InteractiveHistogram)
const LOG_TYPE_COLORS = {
  conn: '#7357D2',
  dns: '#4A90D9',
  http: '#50C878',
  ssl: '#F5A623',
  x509: '#D94A4A',
  files: '#9B59B6',
  smtp: '#1ABC9C',
  ssh: '#E74C3C',
  dhcp: '#3498DB',
  ntp: '#95A5A6',
  rdp: '#E67E22',
  ftp: '#27AE60',
  sip: '#8E44AD',
  snmp: '#2980B9',
  kerberos: '#C0392B',
  smb: '#16A085',
  dce_rpc: '#D35400',
  ntlm: '#7F8C8D',
  pe: '#BDC3C7',
  weird: '#E91E63',
  notice: '#FF5722',
  software: '#607D8B',
  known: '#795548',
  _default: '#6B7280'
}

function getLogTypeColor(logType) {
  return LOG_TYPE_COLORS[logType] || LOG_TYPE_COLORS._default
}

function formatCount(count) {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
  return count.toLocaleString()
}

onMounted(() => {
  // Load saved state from localStorage (separate key from main sidebar)
  const saved = localStorage.getItem('webscout-tool-sidebar-expanded')
  if (saved !== null) {
    isExpanded.value = saved === 'true'
  }
  // Emit initial state
  emit('update:expanded', isExpanded.value)
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  localStorage.setItem('webscout-tool-sidebar-expanded', isExpanded.value.toString())
  emit('update:expanded', isExpanded.value)
}

function handleExportAll() {
  emit('export', selectedFormat.value)
}

function handleExportLogType(logType) {
  emit('export-log-type', logType, selectedFormat.value)
}

function openFieldGuide() {
  mainStore.show_slideover(ZeekFieldGuide, {}, { width: 40 }, 'Zeek Field Guide')
}
</script>

<template>
  <div class="tool-sidebar-wrapper">
    <nav id="tool-sidebar" :class="{ 'expanded': isExpanded }">

      <!-- Top Section - Placeholder tools -->
      <ul class="navbar-nav navbar-top" ondragstart="return false;">
        <li class="nav-item-wrapper">
          <router-link v-if="hasExecutedQuery" class="sidebar-nav" :to="tableLink">
            <font-awesome-icon class="icon" :icon="['fas', 'table']" />
            <span class="nav-text">table</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>table</span>
            </div>
          </router-link>
          <div v-else class="sidebar-nav disabled">
            <font-awesome-icon class="icon" :icon="['fas', 'table']" />
            <span class="nav-text">table</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>table</span>
            </div>
          </div>
        </li>
        <li class="nav-item-wrapper">
          <router-link v-if="hasExecutedQuery" class="sidebar-nav" :to="aggregationsLink">
            <font-awesome-icon class="icon" :icon="['fas', 'chart-bar']" />
            <span class="nav-text">aggregations</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>aggregations</span>
            </div>
          </router-link>
          <div v-else class="sidebar-nav disabled">
            <font-awesome-icon class="icon" :icon="['fas', 'chart-bar']" />
            <span class="nav-text">aggregations</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>aggregations</span>
            </div>
          </div>
        </li>
      </ul>

      <!-- Middle Section - Export -->
      <ul class="navbar-nav mt-auto mb-auto" ondragstart="return false;">
        <SidebarDropdown
          :icon="['fas', 'download']"
          label="export"
          header-label="Export"
          header-subtext="max 10,000 documents"
          :disabled="!canExport || loading"
          :loading="exportLoading"
          loading-text="Exporting..."
          :sidebar-expanded="isExpanded"
        >
          <div class="format-toggle">
            <button
              class="format-btn"
              :class="{ active: selectedFormat === 'jsonl' }"
              @click="selectedFormat = 'jsonl'"
            >JSONL</button>
            <button
              class="format-btn"
              :class="{ active: selectedFormat === 'csv' }"
              @click="selectedFormat = 'csv'"
            >CSV</button>
          </div>

          <div class="dropdown-divider"></div>

          <div class="dropdown-item" @click="handleExportAll">
            <font-awesome-icon :icon="['fas', 'file-export']" class="item-icon" />
            <span>Export all</span>
          </div>

          <div v-if="logTypeDistribution.length > 0" class="dropdown-divider"></div>

          <div v-if="logTypeDistribution.length > 0" class="dropdown-section">By log type</div>

          <div
            v-for="dist in logTypeDistribution"
            :key="dist.log_type"
            class="dropdown-item log-type-item"
            @click="handleExportLogType(dist.log_type)"
          >
            <span
              class="log-type-indicator"
              :style="{ backgroundColor: getLogTypeColor(dist.log_type) }"
            ></span>
            <span class="log-type-name">{{ dist.log_type }}</span>
            <span class="log-type-count" :class="{ 'over-limit': dist.count > 10000 }">
              {{ formatCount(dist.count) }}
              <font-awesome-icon
                v-if="dist.count > 10000"
                :icon="['fas', 'triangle-exclamation']"
                class="warning-icon"
              />
            </span>
          </div>
        </SidebarDropdown>
      </ul>

      <!-- Bottom Section - History, Query Guide, Save, Toggle -->
      <ul class="navbar-nav navbar-bottom">
        <!-- Query History -->
        <li class="nav-item-wrapper">
          <router-link class="sidebar-nav" :to="historyLink">
            <font-awesome-icon class="icon" :icon="['fas', 'clock-rotate-left']" />
            <span class="nav-text">query history</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>query history</span>
            </div>
          </router-link>
        </li>

        <!-- Query Guide -->
        <li class="nav-item-wrapper">
          <div @click="openFieldGuide" class="sidebar-nav">
            <font-awesome-icon class="icon" :icon="['fas', 'question']" />
            <span class="nav-text">query guide</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>query guide</span>
            </div>
          </div>
        </li>

        <!-- Save Search Dropdown -->
        <SaveSearchDropdown
          :sidebar-expanded="isExpanded"
          :disabled="!hasExecutedQuery"
          :query="currentQuery"
          :saved-search-id="savedSearchId"
          :saved-search-name="savedSearchName"
          :saved-search-description="savedSearchDescription"
          :saved-search-query="savedSearchQuery"
          :loading="saveLoading"
          :delete-loading="deleteLoading"
          :is-subscribed="isSubscribed"
          :subscription-loading="subscriptionLoading"
          @save="$emit('save-search', $event)"
          @overwrite="$emit('overwrite-search', $event)"
          @delete="$emit('delete-search')"
          @subscribe="$emit('subscribe')"
          @unsubscribe="$emit('unsubscribe')"
        />

        <!-- Toggle -->
        <li class="nav-item-wrapper">
          <div @click="toggleExpanded()" class="sidebar-nav toggle-nav">
            <font-awesome-icon class="icon" :icon="['fas', isExpanded ? 'angle-right' : 'angle-left']" />
            <span class="nav-text">{{ isExpanded ? 'collapse' : 'expand' }}</span>
            <div class="nav-popout" v-if="!isExpanded">
              <span>expand</span>
            </div>
          </div>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style scoped>
.tool-sidebar-wrapper {
  position: relative;
  padding: 1rem;
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  z-index: 10;
  transition: width 0.3s ease;
  background-color: #191f27;
}

#tool-sidebar {
  padding: 0;
  background: #151a22;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 85px;
  height: calc(100%);
  border-radius: 1rem;
  transition: width 0.3s ease;
}

#tool-sidebar.expanded {
  width: 260px;
}

.section-label {
  padding: 0.75rem 1rem !important;
  margin-bottom: 0.25rem;
}

.section-text {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #4a5568;
  font-weight: 600;
}

.nav-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper {
  padding: 0.25rem 0.75rem;
}

#tool-sidebar .sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  position: relative;
  gap: 0;
}

#tool-sidebar .sidebar-nav.disabled {
  color: #4a5568;
  cursor: not-allowed;
}

#tool-sidebar:not(.expanded) .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#tool-sidebar .sidebar-nav .icon {
  transition: margin-left 0.3s ease;
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Text visibility in collapsed state */
#tool-sidebar:not(.expanded) .nav-text {
  display: none;
}

/* Text visibility in expanded state */
#tool-sidebar.expanded .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .sidebar-nav .icon {
  margin-right: 0.75rem;
  transition: all 0.2s ease;
}

/* Popout styles for collapsed state - positioned to the LEFT */
.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: #1d2531;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

/* Show popout and background on hover of nav-item-wrapper */
#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav:not(.disabled) {
  background-color: #1d2531;
  color: white;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav.disabled {
  background-color: #1a1f28;
}

/* Muted popout for disabled items - applied at base so fade-out doesn't flash enabled colors */
#tool-sidebar .sidebar-nav.disabled .nav-popout {
  background: #1a1f28;
  color: #4a5568;
}

.nav-popout:hover {
  color: white;
  text-decoration: none;
}

/* Prevent popout hover from overriding disabled colors */
#tool-sidebar .sidebar-nav.disabled .nav-popout:hover {
  color: #4a5568;
}

/* Hover effects - same for both collapsed and expanded */
#tool-sidebar .sidebar-nav:not(.disabled):hover {
  color: white;
  cursor: pointer;
  background-color: #1d2531;
}

#tool-sidebar .sidebar-nav.disabled:hover {
  background-color: #1a1f28;
}

/* Active state - use exact matching only */
.router-link-exact-active {
  color: white !important;
}

.router-link-exact-active.sidebar-nav {
  background-color: #1d2531;
  color: white !important;
}

/* Toggle nav styling */
.toggle-nav {
  color: #7A8696;
}

/* Top and bottom spacing for navbar sections */
.navbar-top {
  padding-top: 0.5rem;
}

.navbar-bottom {
  padding-bottom: 0.5rem;
}
</style>

<!-- Unscoped styles for export dropdown menu items -->
<style>
.sidebar-dropdown-menu .log-type-item {
  padding: 0.5rem 1rem;
}

.sidebar-dropdown-menu .log-type-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sidebar-dropdown-menu .log-type-name {
  flex: 1;
}

.sidebar-dropdown-menu .log-type-count {
  font-size: 0.75rem;
  color: #6B7280;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.sidebar-dropdown-menu .log-type-count.over-limit {
  color: #F59E0B;
}

.sidebar-dropdown-menu .warning-icon {
  font-size: 0.7rem;
}

.sidebar-dropdown-menu .format-toggle {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
}

.sidebar-dropdown-menu .format-btn {
  flex: 1;
  padding: 0.35rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  background: transparent;
  color: #6B7280;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sidebar-dropdown-menu .format-btn:hover {
  border-color: rgba(115, 87, 210, 0.4);
  color: #E5E7EB;
}

.sidebar-dropdown-menu .format-btn.active {
  background: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.5);
  color: #E5E7EB;
}
</style>
